import { Box, IconButton, Typography } from '@mui/material';
import { Drawer } from '../../../stories/containers/Drawer/Drawer';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { surveyActions, surveysSelectors } from 'redux/slices/surveys.slice';
import { selectSelectedMember } from 'redux/slices/memberSlice.slice';
import { Button, IconLibrary, ReemaThemeV2 } from '@act/storybook';
import { surveysRequests } from 'redux/requests/surveys.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '../../app/ReusableToast';
import {
  AssignedSurveyContent,
  notesSchema,
  NotesForm,
} from './AssignedSurveyContent';
import { GoalsSidePanel } from '@act/react-components/needs/need-detail-dialog/GoalsSidePanel';
import { useMobile } from '@act/utilities/MobileContext';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { useTracking } from '@act/react-components/tracking';
import { setSidePanelIsDirty } from '@act/react-components/member-page/Calls/FullScreenSaveClose.utils';
import useUnsavedChangesDialog from '../hooks/useUnsavedChangesDialog.hook';

interface SurveyDrawerProps {
  open: boolean;
  onClose: () => void;
  survey: SurveyV2.AssignedSurveyDto;
  isReviewMode?: boolean;
  onAnimationComplete?: () => void;
}

export const SurveyDrawer = ({
  open,
  onClose,
  survey,
  isReviewMode = false,
  onAnimationComplete,
}: SurveyDrawerProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const isMobile = useMobile();
  const { generateTrackingProps } = useTracking();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const needsCallFF = useFeatureFlag('needs-call');
  const member: UserDto = useAppSelector(selectSelectedMember);
  const assignedSurvey = surveysSelectors.useSelectAssignedSurveyById(
    survey.id
  );
  const { control, getValues, formState, reset } = useForm<NotesForm>({
    defaultValues: {
      notes: assignedSurvey?.notes ?? '<p><br></p>',
    },
    resolver: zodResolver(notesSchema),
  });

  const [submittingForm, setSubmittingForm] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [updatedAssignedSurvey, _isUpdatingAssignedSurvey, updateNote] =
    useApiCall(surveysRequests.updateAssignedSurveyNotes);
  const [submittedSurvey, _submitSurveyLoading, submitSurvey] = useApiCall(
    surveysRequests.submitAssignedSurvey
  );
  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setFormIsDirty(isDirty);
  }, []);
  async function handleAutoSave(autoSaveNote: string) {
    if (autoSaveNote !== assignedSurvey?.notes) {
      updateNote({
        params: { assignedSurveyId: assignedSurvey.id },
        body: {
          notes: autoSaveNote,
        },
      });
    }
  }

  async function handleSubmit() {
    setSubmittingForm(true);
    if (!isReviewMode) {
      submitSurvey({
        params: { assignedSurveyId: assignedSurvey.id },
        body: null,
      });
    }
    const notes = getValues('notes');
    updateNote({
      params: { assignedSurveyId: assignedSurvey.id },
      body: { notes },
    });
  }

  useEffect(() => {
    if (submittedSurvey) {
      dispatch(surveyActions.upsertAssignedSurvey(submittedSurvey));
      toast('Successfully saved survey');
      onClose();
    }
    if (submittingForm && updatedAssignedSurvey) {
      const currentAssignedSurvey = {
        ...assignedSurvey,
        ...{ notes: updatedAssignedSurvey.notes },
      };
      dispatch(surveyActions.upsertAssignedSurvey(currentAssignedSurvey));
      toast('Successfully updated survey');
      onClose();
    }
  }, [submittedSurvey, updatedAssignedSurvey]);

  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
    }
  }, [open]);

  const { isDirty } = formState;
  useEffect(() => {
    if (open) {
      if (formIsDirty) {
        setSidePanelIsDirty(true);
      } else {
        setSidePanelIsDirty(isDirty);
      }
    }
  }, [isDirty, open, formIsDirty]);

  useEffect(() => {
    if (!open) {
      setSidePanelIsDirty(false);
    }
    return () => {
      setSidePanelIsDirty(false);
    };
  }, [open]);

  const handleAnimationComplete = () => {
    setShouldRender(false);
    onAnimationComplete?.();
  };

  const handleClose = () => {
    if (isDirty || formIsDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  if (!assignedSurvey) {
    return null;
  }

  if (needsCallFF) {
    if (!shouldRender) return null;
    return (
      <GoalsSidePanel
        open={open}
        animated={true}
        setShouldRenderSidePanel={handleAnimationComplete}
        titleContent={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'space-between',
              justifyContent: 'space-between',
              gap: '10px',
              padding: '8px',
              borderBottom: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`,
            }}
          >
            <Typography
              variant="titleLarge"
              sx={{
                fontSize: isMobile ? '1.25rem' : undefined,
                lineHeight: isMobile ? 1.3 : 2.0,
              }}
            >
              {isReviewMode ? 'Reviewing survey' : 'Survey'} for{` `}
              {member?.firstName} {member?.lastName?.slice(0, 1)}
            </Typography>
            <IconButton onClick={handleClose}>
              <IconLibrary
                iconName="X"
                size={20}
                weight="regular"
                style={{ alignSelf: 'flex-end' }}
              />
            </IconButton>
          </Box>
        }
        content={
          <>
            <Box sx={{ flex: 1, overflow: 'auto', padding: '0 12px' }}>
              <AssignedSurveyContent
                assignedSurvey={assignedSurvey}
                member={member}
                isReviewMode={isReviewMode}
                submittingForm={submittingForm}
                control={control}
                formState={formState}
                handleAutoSave={handleAutoSave}
                onFormStateChange={handleFormStateChange}
              />
            </Box>
          </>
        }
        footerContent={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleSubmit}
              {...generateTrackingProps('survey-from-call-submit-button')}
            >
              {submittingForm ? 'Saving...' : 'Save and close'}
            </Button>
          </Box>
        }
      />
    );
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={`${isReviewMode ? 'Reviewing survey' : 'Survey'} for ${
          member?.firstName
        } ${member?.lastName?.slice(0, 1)}`}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto', padding: '0 12px' }}>
            <AssignedSurveyContent
              assignedSurvey={assignedSurvey}
              member={member}
              isReviewMode={isReviewMode}
              submittingForm={submittingForm}
              control={control}
              formState={formState}
              handleAutoSave={handleAutoSave}
              onFormStateChange={handleFormStateChange}
            />
          </Box>
          <Box
            sx={{
              padding: '16px 24px',
              marginTop: 'auto',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              type="submit"
              debounced
              onClick={handleSubmit}
              disabled={submittingForm}
              sx={{ padding: '8px 24px' }}
              {...generateTrackingProps('survey-from-call-submit-button')}
            >
              {submittingForm ? 'Saving...' : 'Save and close'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
